import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Handshake, Check, Users, Eye, Megaphone, Trophy } from "lucide-react";
import { SponsorPayDialog } from "@/components/sponsorship/SponsorPayDialog";
import { SponsorEnquiryForm } from "@/components/sponsorship/SponsorEnquiryForm";

const REACH = [
  { icon: Users, label: "Families & players", text: "Thousands of Canadian ballplayers and parents use CBR every season." },
  { icon: Eye, label: "Scouts & coaches", text: "College recruiters and programs browse CBR rankings and profiles." },
  { icon: Megaphone, label: "Social reach", text: "Shout-outs across CBR's growing social channels." },
  { icon: Trophy, label: "Events & trips", text: "Your brand at CBR camps, showcases and CBR Nation trips." },
];

export const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { maximumFractionDigits: 0 })}`;

export default function Sponsorship() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("");
  const [paying, setPaying] = useState(null);
  const [params] = useSearchParams();
  const formRef = useRef(null);

  useEffect(() => {
    api.get("/sponsorship/packages").then(({ data }) => setPackages(data.packages || [])).catch(() => {});
    if (params.get("canceled")) setTimeout(() => toast.info("Payment cancelled — no charge was made."), 300);
  }, [params]);

  const enquire = (id) => { setSelected(id); formRef.current?.scrollIntoView({ behavior: "smooth" }); };

  const startCheckout = async (form) => {
    try {
      const { data } = await api.post("/sponsorship/checkout", { ...form, package_id: paying.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not start checkout"); }
  };

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-14">
          <div className="flex items-center gap-2 label-overline text-[#E63946]"><Handshake className="w-4 h-4" /> Partner With CBR</div>
          <h1 data-testid="sponsorship-title" className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mt-3">Become a Sponsor</h1>
          <p className="mt-5 text-white/70 max-w-2xl leading-relaxed">Put your brand in front of Canada's next generation of ballplayers, their families and the scouts who follow them.</p>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {REACH.map((r) => (
              <div key={r.label} className="border border-white/10 bg-white/[0.04] rounded-sm p-5">
                <r.icon className="w-5 h-5 text-[#E63946]" />
                <div className="font-display uppercase tracking-tight mt-3">{r.label}</div>
                <p className="text-xs text-white/60 mt-1 leading-relaxed">{r.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F1F3F6] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-[#E63946]">Packages</div>
          <h2 className="font-display text-3xl uppercase tracking-tight mt-2 text-[#0A192F]">Choose Your Partnership</h2>
          <div className="mt-8 grid md:grid-cols-3 gap-6" data-testid="sponsor-packages">
            {packages.map((p) => <PackageCard key={p.id} p={p} onPay={() => setPaying(p)} onEnquire={() => enquire(p.id)} />)}
          </div>
          <p className="text-xs text-slate-500 mt-4">Prices in CAD, per season. Want something custom? Send us an enquiry below.</p>
        </div>
      </section>

      <section ref={formRef} className="bg-white py-16" id="enquire">
        <div className="max-w-3xl mx-auto px-6">
          <div className="label-overline text-[#E63946]">Enquire</div>
          <h2 className="font-display text-3xl uppercase tracking-tight mt-2 text-[#0A192F]">Talk to the CBR Team</h2>
          <p className="text-sm text-slate-500 mt-2">Tell us about your business and we'll get back to you within 2 business days.</p>
          <SponsorEnquiryForm packages={packages} selected={selected} onSelect={setSelected} />
        </div>
      </section>

      <SponsorPayDialog pkg={paying} onClose={() => setPaying(null)} onSubmit={startCheckout} />
    </PublicLayout>
  );
}

function PackageCard({ p, onPay, onEnquire }) {
  return (
    <div data-testid={`sponsor-package-${p.id}`}
      className={`relative bg-white rounded-sm p-7 flex flex-col border-2 transition-transform duration-300 hover:-translate-y-1 ${p.featured ? "border-[#E63946] shadow-xl" : "border-slate-200"}`}>
      {p.featured && <span className="absolute -top-3 left-7 bg-[#E63946] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-sm">Most Popular</span>}
      <div className="label-overline text-slate-500">{p.tagline}</div>
      <div className="font-display text-3xl uppercase tracking-tight text-[#0A192F] mt-1">{p.name}</div>
      <div className="mt-4 font-data text-4xl text-[#0A192F]" data-testid={`sponsor-price-${p.id}`}>{money(p.price_cad)}<span className="text-sm text-slate-400 ml-1">CAD</span></div>
      <ul className="mt-6 space-y-2.5 flex-1">
        {(p.perks || []).map((perk) => (
          <li key={perk} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-[#E63946] shrink-0 mt-0.5" />{perk}</li>
        ))}
      </ul>
      <button data-testid={`sponsor-pay-${p.id}`} onClick={onPay}
        className="mt-7 bg-[#E63946] hover:bg-[#D90429] text-white px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">Pay Now</button>
      <button data-testid={`sponsor-enquire-${p.id}`} onClick={onEnquire}
        className="mt-2 border border-slate-300 hover:bg-slate-50 px-5 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">Ask a Question</button>
    </div>
  );
}
